/**
 * CP-14.2b — Workforce Offering (Crew Offering) editor.
 *
 * Replaces the single-hourly-rate assumption. Each row binds a resource type
 * (title_taxonomy code) to a quantity, hourly_rate, currency, and the
 * `site_lead_capable` first-class flag. Solo contractors typically have one
 * row; companies will have many.
 *
 * Phase 12 directory.contractor_profile.hourly_rate is intentionally kept
 * intact. To preserve booking flows that still read the Phase-12 column,
 * the page also mirrors the primary lead row's rate into the legacy field
 * on save (handled by the parent component).
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";

const CURRENCIES = ["USD", "CAD", "EUR", "GBP", "AUD"];

function emptyRow() {
  return {
    title_code: "",
    quantity_available: 1,
    hourly_rate: "",
    currency: "USD",
    site_lead_capable: false,
    notes: "",
  };
}

export function WorkforceOfferingEditor({ ownerKind = "user", orgId = null, solo = false, onSaved }) {
  const [titles, setTitles] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const endpoint =
    ownerKind === "org" && orgId
      ? `/orgs/${orgId}/workforce-offerings`
      : `/me/workforce-offerings`;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [tax, mine] = await Promise.all([
          api("/title-taxonomy"),
          api(endpoint),
        ]);
        setTitles(tax.items || []);
        const items = (mine.items || []).map((it) => ({
          ...it,
          hourly_rate: String(it.hourly_rate ?? ""),
          notes: it.notes || "",
        }));
        setRows(items.length ? items : [emptyRow()]);
      } catch (e) {
        toast.error(`Could not load offerings: ${e.message}`);
        setRows([emptyRow()]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  const usedTitleCodes = useMemo(
    () => new Set(rows.map((r) => r.title_code).filter(Boolean)),
    [rows],
  );

  const updateRow = (idx, patch) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx) => {
    setRows((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.length ? next : [emptyRow()];
    });
  };

  const save = async () => {
    // Client-side validation
    const cleaned = rows.filter(
      (r) => r.title_code && r.hourly_rate !== "" && r.hourly_rate !== null,
    );
    if (cleaned.length === 0) {
      toast.error("Add at least one offering row with a resource type and rate.");
      return;
    }
    const codes = cleaned.map((r) => r.title_code);
    if (new Set(codes).size !== codes.length) {
      toast.error("Duplicate resource type. Each row must use a unique resource type.");
      return;
    }
    const payload = {
      items: cleaned.map((r, i) => ({
        title_code: r.title_code,
        quantity_available: solo ? 1 : Number(r.quantity_available) || 1,
        hourly_rate: Number(r.hourly_rate),
        currency: (r.currency || "USD").toUpperCase(),
        site_lead_capable: !!r.site_lead_capable,
        notes: r.notes || null,
        display_order: i,
        active: true,
      })),
    };
    setBusy(true);
    try {
      const r = await api(endpoint, { method: "PUT", body: payload });
      toast.success(`Saved ${r.count} offering row${r.count === 1 ? "" : "s"}`);
      onSaved && onSaved(payload.items);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-[var(--avcp-ink-soft)]">Loading offerings…</div>;
  }

  return (
    <div data-testid={TID.cpStepOffering}>
      <p className="text-sm text-[var(--avcp-ink-soft)] mb-3">
        {solo ? <>List the roles you can personally fill, including your technician level and rate. Each row is an alternative role for the same person. Mark <strong>Site lead</strong> if you can lead the job.</> : <>List the roles you can provide, with a quantity and hourly rate. Mark <strong>Site lead</strong> for a role that can run the work on site.</>}
      </p>

      <div className="overflow-x-auto" data-testid={TID.cpOfferingTable}>
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[var(--avcp-ink-soft)] border-b border-[var(--avcp-line)]">
              <th className="py-2 pr-3">Resource type</th>
              <th className="py-2 pr-3 w-20">Qty</th>
              <th className="py-2 pr-3 w-32">Hourly rate</th>
              <th className="py-2 pr-3 w-24">Currency</th>
              <th className="py-2 pr-3 w-28">Site lead</th>
              <th className="py-2 pr-3">Notes</th>
              <th className="py-2 pr-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx}
                  data-testid={`${TID.cpOfferingRow}-${idx}`}
                  className="border-b border-[var(--avcp-line)] align-top">
                <td className="py-2 pr-3">
                  <select
                    className="avcp-input"
                    data-testid={`${TID.cpOfferingTitleSelect}-${idx}`}
                    value={r.title_code}
                    onChange={(e) => updateRow(idx, { title_code: e.target.value })}
                  >
                    <option value="">— Select —</option>
                    {titles.map((t) => {
                      const disabled =
                        t.code !== r.title_code && usedTitleCodes.has(t.code);
                      return (
                        <option key={t.code} value={t.code} disabled={disabled}>
                          {t.display_label_default}
                          {disabled ? " (used above)" : ""}
                        </option>
                      );
                    })}
                  </select>
                </td>
                <td className="py-2 pr-3">
                  <input
                    type="number"
                    min="1"
                    max={solo ? "1" : "9999"}
                    disabled={solo}
                    className="avcp-input"
                    data-testid={`${TID.cpOfferingQty}-${idx}`}
                    value={solo ? 1 : r.quantity_available}
                    onChange={(e) => updateRow(idx, { quantity_available: e.target.value })}
                  />
                </td>
                <td className="py-2 pr-3">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="avcp-input"
                    data-testid={`${TID.cpOfferingRate}-${idx}`}
                    value={r.hourly_rate}
                    onChange={(e) => updateRow(idx, { hourly_rate: e.target.value })}
                  />
                </td>
                <td className="py-2 pr-3">
                  <select
                    className="avcp-input"
                    data-testid={`${TID.cpOfferingCurrency}-${idx}`}
                    value={r.currency || "USD"}
                    onChange={(e) => updateRow(idx, { currency: e.target.value })}
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 pr-3">
                  <label className="inline-flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      data-testid={`${TID.cpOfferingSiteLead}-${idx}`}
                      checked={!!r.site_lead_capable}
                      onChange={(e) => updateRow(idx, { site_lead_capable: e.target.checked })}
                    />
                    <span title="Can run job independently / Site Lead capable">
                      can run job
                    </span>
                  </label>
                </td>
                <td className="py-2 pr-3">
                  <input
                    type="text"
                    placeholder="Optional"
                    className="avcp-input"
                    data-testid={`${TID.cpOfferingNotes}-${idx}`}
                    value={r.notes}
                    onChange={(e) => updateRow(idx, { notes: e.target.value })}
                  />
                </td>
                <td className="py-2 pr-3">
                  {rows.length > 1 && (
                    <button
                      type="button"
                      className="text-[var(--avcp-ink-soft)] hover:text-red-600"
                      data-testid={`${TID.cpOfferingRemove}-${idx}`}
                      title="Remove row"
                      onClick={() => removeRow(idx)}
                    >
                      ✕
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button
          type="button"
          className="avcp-btn"
          data-testid={TID.cpOfferingAddRow}
          onClick={addRow}
        >
          + Add resource type
        </button>
        <button
          type="button"
          className="avcp-btn primary"
          data-testid={TID.cpOfferingSave}
          disabled={busy}
          onClick={save}
        >
          {busy ? "Saving…" : "Save workforce offering"}
        </button>
      </div>
    </div>
  );
}

export default WorkforceOfferingEditor;
